"use client";

import { useState, useCallback, useMemo, useRef } from "react";
import type {
  Guest,
  DeliveryAddress,
  PersonalDetailsState,
  CheckoutStep,
  UserFormAnswer,
  AddOnSelection,
  AddOnGroup,
} from "../utils/types";
import type { CheckoutParticipant } from "../participants-step";

// Default guest
export const defaultGuest: Guest = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  marketingOptIn: false,
  mandatoryTickBox: false,
  guest: false,
};

// Default delivery address
export const defaultDeliveryAddress: DeliveryAddress = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  postCode: "",
  country: "",
};

// Base checkout state
export type BaseCheckoutState = {
  step: CheckoutStep;
  addOnSelections: AddOnSelection[];
  userFormValues: UserFormAnswer[];
  personalDetails: PersonalDetailsState;
  discountCode: string;
  isLoading: boolean;
  isConfirmingFreeBooking: boolean;
  isDiscountEnabled: boolean;
  isUserFormValid: boolean;
  error: string | null;
};

// Base hook props
export type UseCheckoutBaseProps = {
  user?: any;
  enabledAddOns?: any[];
  addOnGroups?: AddOnGroup[];
  isSendingMaterial?: boolean;
  onStepChange?: () => void;
};

/**
 * Base hook for common checkout functionality
 */
export function useCheckoutBase({
  user,
  enabledAddOns = [],
  addOnGroups = [],
  isSendingMaterial = false,
  onStepChange,
}: UseCheckoutBaseProps) {
  // Initialize personal details from user
  const initialGuest = useMemo((): Guest => {
    if (user?.profile) {
      return {
        ...defaultGuest,
        firstName: user.profile.firstName || "",
        lastName: user.profile.lastName || "",
        email: user.emails?.[0]?.address || "",
        phoneNumber: user.profile.phone || "",
      };
    }
    return { ...defaultGuest };
  }, [user]);

  // Add-on selection handlers
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelection[]>([]);

  const updateAddOnSelection = useCallback(
    (
      addOnId: string,
      quantity: number,
      groupId?: string | null,
      groupName?: string | null,
    ) => {
      setAddOnSelections((prev) => {
        const existingIndex = prev.findIndex((sel) => sel.addOnId === addOnId);

        if (quantity === 0) {
          return prev.filter((sel) => sel.addOnId !== addOnId);
        } else if (existingIndex >= 0) {
          const newSelections = [...prev];
          newSelections[existingIndex] = {
            addOnId,
            quantity,
            groupId,
            groupName,
          };
          return newSelections;
        } else {
          return [...prev, { addOnId, quantity, groupId, groupName }];
        }
      });
    },
    [],
  );

  // User form handlers
  const [userFormValues, setUserFormValues] = useState<UserFormAnswer[]>([]);
  const [isUserFormValid, setIsUserFormValid] = useState(false);

  // Participants (attendees) step state — shared by all flows.
  const [participantsValue, setParticipantsValue] = useState<CheckoutParticipant[]>([]);
  const [isParticipantsValid, setIsParticipantsValid] = useState(false);
  // Persists the buyer-as-attendee auto-seed across ParticipantsStep remounts
  // (the step unmounts on Back/Next). Kept HERE, not in the step, so removing
  // the auto-added buyer and navigating away can't silently re-add them.
  const buyerAttendeeSeededRef = useRef(false);

  const updateUserFormValue = useCallback(
    (
      questionIndex: number,
      guestIndex: number | null,
      label: string,
      value: string | boolean | string[] | null,
    ) => {
      setUserFormValues((prev) => {
        const existingIndex = prev.findIndex(
          (item) =>
            item.index === questionIndex && item.guestIndex === guestIndex,
        );

        if (value === null || value === "") {
          return prev.filter(
            (item) =>
              !(item.index === questionIndex && item.guestIndex === guestIndex),
          );
        } else if (existingIndex !== -1) {
          const newValues = [...prev];
          newValues[existingIndex] = {
            index: questionIndex,
            guestIndex,
            label,
            value,
          };
          return newValues;
        } else {
          return [...prev, { index: questionIndex, guestIndex, label, value }];
        }
      });
    },
    [],
  );

  // Personal details handlers
  const [personalDetails, setPersonalDetails] = useState<PersonalDetailsState>(
    () => ({
      guests: [initialGuest],
      deliveryAddress: isSendingMaterial
        ? { ...defaultDeliveryAddress }
        : undefined,
    }),
  );

  const updatePersonalDetails = useCallback(
    (guestIndex: number, field: keyof Guest, value: string | boolean) => {
      setPersonalDetails((prev) => {
        const newGuests = [...prev.guests];
        if (newGuests[guestIndex]) {
          newGuests[guestIndex] = {
            ...newGuests[guestIndex]!,
            [field]: value,
          };
        }
        return { ...prev, guests: newGuests };
      });
    },
    [],
  );

  const updateDeliveryAddress = useCallback(
    (field: keyof DeliveryAddress, value: string) => {
      setPersonalDetails((prev) => ({
        ...prev,
        deliveryAddress: {
          ...defaultDeliveryAddress,
          ...prev.deliveryAddress,
          [field]: value,
        },
      }));
    },
    [],
  );

  // Discount handlers
  const [discount, setDiscount] = useState<{
    value: number;
    type?: "PERCENTAGE" | "PRICE";
    percentage?: number;
    /** DEV-327: the code leaves add-ons at full price. */
    excludeAddOns?: boolean;
  } | null>(null);
  const [discountCode, setDiscountCode] = useState("");

  // Error handling
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmingFreeBooking, setIsConfirmingFreeBooking] = useState(false);

  // Calculate add-ons total (standalone + grouped)
  const addOnsTotal = useMemo(() => {
    // Build a lookup of all add-ons (standalone + inside groups)
    const allAddOns = [
      ...enabledAddOns,
      ...addOnGroups.flatMap((g) => g.addOns),
    ];
    return addOnSelections.reduce((sum, selection) => {
      const addOn = allAddOns.find((a: any) => a.id === selection.addOnId);
      return sum + (addOn?.price ?? 0) * selection.quantity;
    }, 0);
  }, [addOnSelections, enabledAddOns, addOnGroups]);

  // Validate that all add-on groups meet min/max selection constraints
  const addOnGroupsValid = useMemo(() => {
    return addOnGroups.every((group) => {
      const groupSelections = addOnSelections.filter(
        (s) => s.groupId === group.id && s.quantity > 0,
      );
      const totalSelected = groupSelections.reduce(
        (sum, s) => sum + s.quantity,
        0,
      );
      if (group.minSelection > 0 && totalSelected < group.minSelection) return false;
      if (group.maxSelection > 0 && totalSelected > group.maxSelection) return false;
      return true;
    });
  }, [addOnGroups, addOnSelections]);

  return {
    // State
    addOnSelections,
    userFormValues,
    personalDetails,
    discount,
    discountCode,
    error,
    isLoading,
    isConfirmingFreeBooking,
    isUserFormValid,
    participantsValue,
    isParticipantsValid,
    buyerAttendeeSeededRef,

    // Computed
    initialGuest,
    addOnsTotal,
    addOnGroupsValid,

    // Actions
    updateAddOnSelection,
    updateUserFormValue,
    setIsUserFormValid,
    updatePersonalDetails,
    updateDeliveryAddress,
    setDiscount,
    setDiscountCode,
    setError,
    clearError,
    setIsLoading,
    setIsConfirmingFreeBooking,
    setPersonalDetails,
    setAddOnSelections,
    setUserFormValues,
    setParticipantsValue,
    setIsParticipantsValid,
  };
}
